import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ClerkLoaded,
  ClerkLoading,
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
  useAuth,
} from "@clerk/clerk-react";
import {
  ShieldCheck,
  Users,
  Building2,
  Key,
  Layers,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  RefreshCw,
  Trash2,
  ExternalLink,
  Package,
  Plus,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { MemberAvatar } from "@/components/MemberAvatar";
import { toast } from "sonner";
import {
  adminIsSuperAdmin,
  adminListUsers,
  adminListOrgs,
  adminListOrgMembers,
  adminListOrgGrants,
  adminPromoteUser,
  adminDemoteUser,
  adminRevokeOrgGrant,
  adminCreateOrg,
  adminGrantOrgAccess,
  adminListCrossOrgRules,
  adminDeleteCrossOrgRule,
  adminApproveCrossOrgRule,
  adminRejectCrossOrgRule,
  adminCreateCrossOrgRule,
  adminListGroups,
  adminCreateGroup,
  adminDeleteGroup,
  adminListUserGroupAssignments,
  adminGetGroupMembers,
  adminAddGroupMember,
  adminRemoveGroupMember,
  adminGetGroupToolRoles,
  adminSetGroupToolRole,
  adminListProducts,
  adminCreateProduct,
  adminDeleteProduct,
  adminGetManageTeamUrl,
  adminGetNotificationEmails,
  adminSetNotificationEmails,
  adminSendTestNotificationEmail,
  type AdminUser,
  type AdminOrg,
  type AdminProduct,
  type OrgGrant,
  type OrgMember,
  type CrossOrgRule,
  type SuiteGroup,
  type GroupMember,
  type GroupToolRole,
  type UserGroupAssignment,
} from "@/lib/adminApi";

// ── Helpers ────────────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, count }: { icon: React.ElementType; title: string; count?: number }) {
  return (
    <div className="flex items-center gap-2 border-b border-slate-200 pb-3 mb-4">
      <Icon className="h-5 w-5 text-indigo-500" />
      <h2 className="text-base font-semibold text-slate-800">{title}</h2>
      {count !== undefined && (
        <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{count}</span>
      )}
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div className="flex-1">{message}</div>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="mr-1 h-3 w-3" /> Retry
        </Button>
      )}
    </div>
  );
}

function Badge({ children, color = "slate" }: { children: React.ReactNode; color?: "slate" | "indigo" | "amber" | "red" | "green" }) {
  const styles: Record<string, string> = {
    slate: "bg-slate-100 text-slate-600 border-slate-200",
    indigo: "bg-indigo-100 text-indigo-700 border-indigo-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    red: "bg-red-100 text-red-700 border-red-200",
    green: "bg-green-100 text-green-700 border-green-200",
  };
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${styles[color]}`}>
      {children}
    </span>
  );
}

// ── Users tab ──────────────────────────────────────────────────────────────

function UsersTab({ getToken }: { getToken: () => Promise<string | null> }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [assignments, setAssignments] = useState<UserGroupAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, a] = await Promise.all([
        adminListUsers(getToken),
        adminListUserGroupAssignments(getToken),
      ]);
      setUsers(u);
      setAssignments(a);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  async function toggle(user: AdminUser) {
    setBusy(user.clerkUserId);
    try {
      if (user.isSuiteAdmin) {
        await adminDemoteUser(getToken, user.clerkUserId);
        toast.success(`Demoted ${user.displayName ?? user.email ?? user.clerkUserId} from suite-admin`);
      } else {
        await adminPromoteUser(getToken, user.clerkUserId);
        toast.success(`Promoted ${user.displayName ?? user.email ?? user.clerkUserId} to suite-admin`);
      }
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(null);
    }
  }

  const userGroupNames = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const a of assignments) {
      if (!map.has(a.clerkUserId)) map.set(a.clerkUserId, []);
      map.get(a.clerkUserId)!.push(a.groupName);
    }
    return map;
  }, [assignments]);

  if (loading) return (
    <ul className="divide-y divide-slate-100 rounded-md border border-slate-100">
      {[...Array(5)].map((_, i) => (
        <li key={i} className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 animate-pulse rounded-full bg-slate-200" />
            <div className="space-y-1.5">
              <div className="h-3.5 w-40 animate-pulse rounded bg-slate-200" />
              <div className="h-3 w-56 animate-pulse rounded bg-slate-100" />
            </div>
          </div>
          <div className="h-7 w-20 animate-pulse rounded bg-slate-100" />
        </li>
      ))}
    </ul>
  );

  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!users) return null;

  const sorted = [...users].sort((a, b) => {
    if (a.isSuperAdmin !== b.isSuperAdmin) return a.isSuperAdmin ? -1 : 1;
    if (a.isSuiteAdmin !== b.isSuiteAdmin) return a.isSuiteAdmin ? -1 : 1;
    return (a.displayName ?? a.email ?? "").localeCompare(b.displayName ?? b.email ?? "");
  });

  return (
    <ul className="divide-y divide-slate-100 rounded-md border border-slate-100">
      {sorted.map((u) => {
        const groupNames = userGroupNames.get(u.clerkUserId) ?? [];
        return (
          <li key={u.clerkUserId} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <MemberAvatar name={u.displayName} imageUrl={null} />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-slate-900">
                  {u.displayName ?? u.email ?? u.clerkUserId}
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {u.email && <span className="text-xs text-slate-400">{u.email}</span>}
                  {u.orgName && (
                    <span className="text-xs text-slate-400">· {u.orgName}</span>
                  )}
                </div>
                {groupNames.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {groupNames.map(gn => (
                      <span key={gn} className="inline-flex items-center gap-0.5 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                        <Layers className="h-2.5 w-2.5" />{gn}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {u.isSuperAdmin && <Badge color="red">super-admin</Badge>}
              {u.isSuiteAdmin && !u.isSuperAdmin && <Badge color="indigo">suite-admin</Badge>}
              {!u.isSuiteAdmin && !u.isSuperAdmin && <Badge>no suite role</Badge>}
              {!u.isSuperAdmin && (
                <Button
                  size="sm"
                  variant={u.isSuiteAdmin ? "outline" : "default"}
                  disabled={busy === u.clerkUserId}
                  onClick={() => void toggle(u)}
                >
                  {busy === u.clerkUserId
                    ? "…"
                    : u.isSuiteAdmin
                    ? "Demote"
                    : "Make suite-admin"}
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ── Orgs tab ───────────────────────────────────────────────────────────────

function OrgGroupItem({
  group,
  assignments,
  getToken,
}: {
  group: SuiteGroup;
  assignments: UserGroupAssignment[];
  getToken: () => Promise<string | null>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [members, setMembers] = useState<GroupMember[] | null>(null);
  const [loading, setLoading] = useState(false);

  const count = useMemo(
    () => assignments.filter(a => a.groupId === group.id).length,
    [assignments, group.id]
  );

  async function toggle() {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (members !== null) return;
    setLoading(true);
    try {
      setMembers(await adminGetGroupMembers(getToken, group.id));
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <li className="divide-y divide-slate-50">
      <button
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 transition-colors"
        onClick={() => void toggle()}
      >
        <Layers className="h-3.5 w-3.5 text-slate-300 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-medium text-slate-800">{group.name}</span>
          {group.description && (
            <span className="ml-1.5 text-xs text-slate-400">{group.description}</span>
          )}
        </div>
        <span className="text-[10px] text-slate-400 flex-shrink-0">{count} member{count !== 1 ? "s" : ""}</span>
        {expanded
          ? <ChevronDown className="h-3 w-3 text-slate-300 flex-shrink-0" />
          : <ChevronRight className="h-3 w-3 text-slate-300 flex-shrink-0" />}
      </button>
      {expanded && (
        <div className="bg-slate-50/70 px-3 pb-2 pt-1">
          {loading && <p className="text-[11px] text-slate-400 py-1">Loading…</p>}
          {members && members.length === 0 && (
            <p className="text-[11px] text-slate-400 italic py-1">No members</p>
          )}
          {members && members.length > 0 && (
            <ul className="space-y-1 pt-0.5">
              {members.map(m => (
                <li key={m.clerkUserId} className="flex items-center gap-1.5">
                  <MemberAvatar name={m.displayName} imageUrl={null} className="h-4 w-4 text-[8px]" />
                  <span className="text-[11px] text-slate-700 truncate">
                    {m.displayName ?? m.email ?? m.clerkUserId}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function OrgRow({
  org,
  groups,
  assignments,
  products,
  getToken,
}: {
  org: AdminOrg;
  groups: SuiteGroup[];
  assignments: UserGroupAssignment[];
  products: AdminProduct[];
  getToken: () => Promise<string | null>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [members, setMembers] = useState<OrgMember[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openingProduct, setOpeningProduct] = useState<string | null>(null);

  const userGroupNames = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const a of assignments) {
      if (!map.has(a.clerkUserId)) map.set(a.clerkUserId, []);
      map.get(a.clerkUserId)!.push(a.groupName);
    }
    return map;
  }, [assignments]);

  async function toggle() {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (members !== null) return;
    setLoading(true);
    setError(null);
    try {
      setMembers(await adminListOrgMembers(getToken, org.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load members");
    } finally {
      setLoading(false);
    }
  }

  async function openManageTeam(productId: string) {
    setOpeningProduct(productId);
    try {
      const url = await adminGetManageTeamUrl(getToken, productId, org.id);
      window.location.href = url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to open Manage Team");
      setOpeningProduct(null);
    }
  }

  return (
    <li className="border-b border-slate-100 last:border-b-0">
      <button
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
        onClick={() => void toggle()}
      >
        <div className="flex items-center gap-3">
          <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-slate-900">{org.name}</div>
            {org.slug && <div className="text-xs text-slate-400">{org.slug}</div>}
          </div>
        </div>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-slate-50 bg-slate-50/50 px-4 pb-3 space-y-3">
          {/* Manage Team — quick-access buttons per registered tool */}
          {products.length > 0 && (
            <div>
              <div className="pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
                <ExternalLink className="h-3.5 w-3.5" /> Manage Team
              </div>
              <div className="flex flex-wrap gap-2">
                {products.map((p) => (
                  <Button
                    key={p.id}
                    size="sm"
                    variant="outline"
                    disabled={openingProduct === p.id}
                    onClick={() => void openManageTeam(p.id)}
                    className="h-7 text-xs gap-1.5"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {openingProduct === p.id ? "Opening…" : p.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Groups — shown first */}
          <div>
            <div className="pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5" /> Groups ({groups.length})
            </div>
            {groups.length === 0 ? (
              <div className="py-2 text-xs text-slate-400">No groups in this org.</div>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-100 bg-white">
                {groups.map((g) => (
                  <OrgGroupItem key={g.id} group={g} assignments={assignments} getToken={getToken} />
                ))}
              </ul>
            )}
          </div>

          {/* Members */}
          <div>
            <div className="pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" /> Members
            </div>
            {loading && <div className="py-2 text-xs text-slate-400">Loading…</div>}
            {error && <ErrorBanner message={error} />}
            {members && members.length === 0 && (
              <div className="py-2 text-xs text-slate-400">No members found.</div>
            )}
            {members && members.length > 0 && (
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-100 bg-white">
                {members.map((m) => {
                  const groupNames = userGroupNames.get(m.clerkUserId) ?? [];
                  return (
                    <li key={m.clerkUserId} className="flex items-center justify-between gap-3 px-3 py-2.5">
                      <div className="flex min-w-0 items-center gap-2">
                        <MemberAvatar name={m.displayName} imageUrl={null} className="h-6 w-6 text-[10px]" />
                        <div className="min-w-0">
                          <div className="truncate text-xs font-medium text-slate-800">
                            {m.displayName ?? m.email ?? m.clerkUserId}
                          </div>
                          {m.email && (
                            <div className="truncate text-xs text-slate-400">{m.email}</div>
                          )}
                          {groupNames.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {groupNames.map(gn => (
                                <span key={gn} className="inline-flex items-center gap-0.5 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                                  <Layers className="h-2.5 w-2.5" />{gn}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Badge color="slate">{m.role === "org:admin" ? "org-admin" : "member"}</Badge>
                        {m.isSuperAdmin && <Badge color="red">super</Badge>}
                        {m.isSuiteAdmin && !m.isSuperAdmin && <Badge color="indigo">suite-admin</Badge>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function OrgsTab({ getToken }: { getToken: () => Promise<string | null> }) {
  const [orgs, setOrgs] = useState<AdminOrg[] | null>(null);
  const [groups, setGroups] = useState<SuiteGroup[]>([]);
  const [assignments, setAssignments] = useState<UserGroupAssignment[]>([]);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [fName, setFName] = useState("");
  const [fSlug, setFSlug] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [o, g, a, p] = await Promise.all([
        adminListOrgs(getToken),
        adminListGroups(getToken),
        adminListUserGroupAssignments(getToken),
        adminListProducts(getToken),
      ]);
      setOrgs(o);
      setGroups(g);
      setAssignments(a);
      setProducts(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load orgs");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!fName.trim()) return;
    setSaving(true);
    try {
      await adminCreateOrg(getToken, { name: fName.trim(), ...(fSlug.trim() ? { slug: fSlug.trim() } : {}) });
      toast.success(`Organisation "${fName.trim()}" created`);
      setShowForm(false);
      setFName(""); setFSlug("");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create organisation");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <ul className="divide-y divide-slate-100 rounded-md border border-slate-100">
      {[...Array(3)].map((_, i) => (
        <li key={i} className="flex items-center gap-3 px-4 py-3">
          <div className="h-4 w-4 animate-pulse rounded bg-slate-200" />
          <div className="h-3.5 w-48 animate-pulse rounded bg-slate-200" />
        </li>
      ))}
    </ul>
  );

  if (error) return <ErrorBanner message={error} onRetry={load} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ Create Org"}
        </Button>
      </div>
      {showForm && (
        <form onSubmit={(e) => void handleCreate(e)} className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Name *</label>
              <input
                value={fName} onChange={e => setFName(e.target.value)} required
                placeholder="Acme Corp"
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Slug (optional)</label>
              <input
                value={fSlug} onChange={e => setFSlug(e.target.value)}
                placeholder="acme-corp"
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving}>{saving ? "Creating…" : "Create organisation"}</Button>
          </div>
        </form>
      )}
      {!orgs || orgs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No organisations found.</p>
      ) : (
        <ul className="rounded-md border border-slate-100">
          {orgs.map((o) => (
            <OrgRow
              key={o.id}
              org={o}
              groups={groups.filter(g => g.orgId === o.id)}
              assignments={assignments.filter(a => a.orgId === o.id)}
              products={products}
              getToken={getToken}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Grants tab ─────────────────────────────────────────────────────────────

const SUITE_ROLES = ["suite-admin", "tool-admin", "tool-manager", "tool-member", "tool-viewer", "denied"] as const;

function GrantsTab({ getToken }: { getToken: () => Promise<string | null> }) {
  const [grants, setGrants] = useState<OrgGrant[] | null>(null);
  const [orgs, setOrgs] = useState<AdminOrg[] | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [groups, setGroups] = useState<SuiteGroup[]>([]);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [fOrg, setFOrg] = useState("");
  const [fTargetType, setFTargetType] = useState<"all" | "user" | "group">("all");
  const [fTargetId, setFTargetId] = useState("");
  const [fProduct, setFProduct] = useState("");
  const [fRole, setFRole] = useState("tool-viewer");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, o, u, gr, p] = await Promise.all([
        adminListOrgGrants(getToken),
        adminListOrgs(getToken),
        adminListUsers(getToken),
        adminListGroups(getToken),
        adminListProducts(getToken),
      ]);
      setGrants(g); setOrgs(o); setUsers(u); setGroups(gr); setProducts(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load grants");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  const orgMap = useMemo(() => new Map((orgs ?? []).map(o => [o.id, o.name])), [orgs]);
  const userMap = useMemo(
    () => new Map((users ?? []).map(u => [u.clerkUserId, u.displayName ?? u.email ?? u.clerkUserId])),
    [users]
  );
  const groupMap = useMemo(() => new Map(groups.map(g => [g.id, g.name])), [groups]);

  async function handleAddGrant(e: React.FormEvent) {
    e.preventDefault();
    if (!fOrg) { toast.error("Select an organisation"); return; }
    if (fTargetType === "user" && !fTargetId) { toast.error("Select a user"); return; }
    if (fTargetType === "group" && !fTargetId) { toast.error("Select a group"); return; }
    setSaving(true);
    try {
      await adminGrantOrgAccess(getToken, {
        orgId: fOrg,
        targetType: fTargetType,
        targetId: (fTargetType === "user" || fTargetType === "group") ? fTargetId : null,
        productId: fProduct || null,
        role: fRole,
      });
      toast.success("Grant created");
      setShowForm(false);
      setFOrg(""); setFTargetType("all"); setFTargetId(""); setFProduct(""); setFRole("tool-viewer");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create grant");
    } finally {
      setSaving(false);
    }
  }

  async function revoke(g: OrgGrant) {
    const key = `${g.orgId}|${g.targetType}|${g.targetId}|${g.productId}`;
    setRevoking(key);
    try {
      await adminRevokeOrgGrant(getToken, {
        orgId: g.orgId,
        targetType: g.targetType,
        targetId: g.targetId,
        productId: g.productId,
      });
      toast.success("Grant revoked");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Revoke failed");
    } finally {
      setRevoking(null);
    }
  }

  if (loading) return (
    <div className="space-y-2">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-12 w-full animate-pulse rounded-md bg-slate-100" />
      ))}
    </div>
  );

  if (error) return <ErrorBanner message={error} onRetry={load} />;

  const usersForOrg = (users ?? []).filter(u => !fOrg || u.orgId === fOrg);
  const groupsForOrg = groups.filter(g => !fOrg || g.orgId === fOrg);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ Add Grant"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={(e) => void handleAddGrant(e)} className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Organisation *</label>
              <select value={fOrg} onChange={e => { setFOrg(e.target.value); setFTargetId(""); }} required
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                <option value="">Select org…</option>
                {(orgs ?? []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Product</label>
              <select value={fProduct} onChange={e => setFProduct(e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                <option value="">All products</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Target</label>
              <select value={fTargetType} onChange={e => { setFTargetType(e.target.value as "all" | "user" | "group"); setFTargetId(""); }}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                <option value="all">All members</option>
                <option value="user">Specific user</option>
                <option value="group">Specific group</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Role</label>
              <select value={fRole} onChange={e => setFRole(e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                {SUITE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {fTargetType === "user" && (
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600 block mb-1">User *</label>
                <select value={fTargetId} onChange={e => setFTargetId(e.target.value)} required
                  className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                  <option value="">Select user…</option>
                  {usersForOrg.map(u => (
                    <option key={u.clerkUserId} value={u.clerkUserId}>
                      {u.displayName ?? u.email ?? u.clerkUserId}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {fTargetType === "group" && (
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600 block mb-1">Group *</label>
                <select value={fTargetId} onChange={e => setFTargetId(e.target.value)} required
                  className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                  <option value="">Select group…</option>
                  {groupsForOrg.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving}>{saving ? "Saving…" : "Create grant"}</Button>
          </div>
        </form>
      )}

      {!grants || grants.length === 0 ? (
        <p className="text-sm text-muted-foreground">No org-suite grants found.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-100">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">Org</th>
                <th className="px-4 py-2.5 text-left font-medium">Target</th>
                <th className="px-4 py-2.5 text-left font-medium">Product</th>
                <th className="px-4 py-2.5 text-left font-medium">Role</th>
                <th className="px-4 py-2.5 text-left font-medium">Granted</th>
                <th className="px-4 py-2.5 text-left font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 bg-white">
              {grants.map((g, i) => {
                const key = `${g.orgId}|${g.targetType}|${g.targetId}|${g.productId}`;
                const orgName = orgMap.get(g.orgId) ?? g.orgId;
                const targetName =
                  g.targetType === "all" ? "All members"
                  : g.targetType === "user" ? (userMap.get(g.targetId ?? "") ?? g.targetId ?? "—")
                  : `Group: ${groupMap.get(g.targetId ?? "") ?? g.targetId ?? "—"}`;
                return (
                  <tr key={i} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2.5 text-xs font-medium text-slate-700 max-w-[160px]">
                      <div className="truncate">{orgName}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-col gap-0.5">
                        <Badge color={g.targetType === "all" ? "amber" : g.targetType === "group" ? "indigo" : "slate"}>
                          {g.targetType}
                        </Badge>
                        <span className="text-[10px] text-slate-500 truncate max-w-[140px]">{targetName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {g.productId ? (products.find(p => p.id === g.productId)?.name ?? g.productId) : <span className="text-slate-400 italic">all</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge color={g.role === "denied" ? "red" : g.role.includes("admin") ? "indigo" : "green"}>
                        {g.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                      {new Date(g.grantedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700 text-xs"
                        disabled={revoking === key}
                        onClick={() => void revoke(g)}
                      >
                        {revoking === key ? "…" : "Revoke"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Cross-org rules tab ─────────────────────────────────────────────────────

const TOOL_MAP_ROLES = ["tool-admin", "tool-manager", "tool-member", "tool-viewer", "denied"] as const;

function CrossOrgRulesTab({ getToken }: { getToken: () => Promise<string | null> }) {
  const [rules, setRules] = useState<CrossOrgRule[] | null>(null);
  const [orgs, setOrgs] = useState<AdminOrg[] | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [groups, setGroups] = useState<SuiteGroup[]>([]);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fProduct, setFProduct] = useState("");
  const [fAdmin, setFAdmin] = useState("tool-viewer");
  const [fManager, setFManager] = useState("tool-viewer");
  const [fMember, setFMember] = useState("tool-viewer");
  const [fAllowedUsers, setFAllowedUsers] = useState<Set<string>>(new Set());
  const [fAllowedGroups, setFAllowedGroups] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, o, u, g, p] = await Promise.all([
        adminListCrossOrgRules(getToken),
        adminListOrgs(getToken),
        adminListUsers(getToken),
        adminListGroups(getToken),
        adminListProducts(getToken),
      ]);
      setRules(r); setOrgs(o); setUsers(u); setGroups(g); setProducts(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load cross-org rules");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  function toggleAllowedUser(id: string) {
    setFAllowedUsers(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleAllowedGroup(id: string) {
    setFAllowedGroups(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function handleAddRule(e: React.FormEvent) {
    e.preventDefault();
    if (!fFrom) { toast.error("Select a from-org"); return; }
    setSaving(true);
    try {
      await adminCreateCrossOrgRule(getToken, {
        fromOrgId: fFrom,
        toOrgId: fTo || null,
        productId: fProduct || null,
        adminMapsTo: fAdmin,
        managerMapsTo: fManager,
        memberMapsTo: fMember,
        allowedUserIds: fAllowedUsers.size > 0 ? [...fAllowedUsers] : null,
        allowedGroupIds: fAllowedGroups.size > 0 ? [...fAllowedGroups] : null,
      });
      toast.success("Cross-org rule created");
      setShowForm(false);
      setFFrom(""); setFTo(""); setFProduct("");
      setFAdmin("tool-viewer"); setFManager("tool-viewer"); setFMember("tool-viewer");
      setFAllowedUsers(new Set()); setFAllowedGroups(new Set());
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create rule");
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => { void load(); }, [load]);

  async function handleApprove(rule: CrossOrgRule) {
    setBusy(rule.id);
    try {
      await adminApproveCrossOrgRule(getToken, rule.id);
      toast.success("Rule approved");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Approve failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleReject(rule: CrossOrgRule) {
    setBusy(rule.id);
    try {
      await adminRejectCrossOrgRule(getToken, rule.id);
      toast.success("Rule rejected");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Reject failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete(rule: CrossOrgRule) {
    setBusy(rule.id);
    try {
      await adminDeleteCrossOrgRule(getToken, rule.id);
      toast.success("Rule deleted");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setBusy(null);
    }
  }

  const userMap = useMemo(() => new Map(users.map(u => [u.clerkUserId, u.displayName ?? u.email ?? u.clerkUserId])), [users]);
  const groupMap = useMemo(() => new Map(groups.map(g => [g.id, g.name])), [groups]);

  const { overriddenByProducts, overridesGlobalRule } = useMemo(() => {
    if (!rules) return { overriddenByProducts: new Map<string, string[]>(), overridesGlobalRule: new Set<string>() };
    const active = rules.filter(r => r.status === "active");
    const globalRules = active.filter(r => r.productId === null);
    const toolRules = active.filter(r => r.productId !== null);
    const overriddenByProducts = new Map<string, string[]>();
    for (const gr of globalRules) {
      const overriding = toolRules.filter(tr =>
        tr.fromOrgId === gr.fromOrgId &&
        (tr.toOrgId === gr.toOrgId || gr.toOrgId === null)
      );
      if (overriding.length > 0) {
        overriddenByProducts.set(gr.id, [...new Set(overriding.map(tr => products.find(p => p.id === tr.productId)?.name ?? tr.productId!))]);
      }
    }
    const overridesGlobalRule = new Set<string>();
    for (const tr of toolRules) {
      const shadows = globalRules.some(gr =>
        gr.fromOrgId === tr.fromOrgId &&
        (gr.toOrgId === tr.toOrgId || gr.toOrgId === null)
      );
      if (shadows) overridesGlobalRule.add(tr.id);
    }
    return { overriddenByProducts, overridesGlobalRule };
  }, [rules, products]);

  if (loading) return (
    <div className="space-y-2">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-14 w-full animate-pulse rounded-md bg-slate-100" />
      ))}
    </div>
  );

  if (error) return <ErrorBanner message={error} onRetry={load} />;

  const statusColor = (s: CrossOrgRule["status"]) =>
    s === "active" ? "green" : s === "pending" ? "amber" : "red";

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ Add Rule"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={(e) => void handleAddRule(e)} className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">From Org *</label>
              <select value={fFrom} onChange={e => setFFrom(e.target.value)} required
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                <option value="">Select org…</option>
                {(orgs ?? []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">To Org</label>
              <select value={fTo} onChange={e => setFTo(e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                <option value="">All orgs</option>
                {(orgs ?? []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Product</label>
              <select value={fProduct} onChange={e => setFProduct(e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                <option value="">All products</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-1" />
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Admin maps to</label>
              <select value={fAdmin} onChange={e => setFAdmin(e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                {TOOL_MAP_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Manager maps to</label>
              <select value={fManager} onChange={e => setFManager(e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                {TOOL_MAP_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Member maps to</label>
              <select value={fMember} onChange={e => setFMember(e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                {TOOL_MAP_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          {/* Optional scope restrictions */}
          {fFrom && (
            <div className="grid grid-cols-2 gap-3 border-t border-slate-200 pt-3">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">
                  Restrict to users <span className="text-slate-400 font-normal">(optional — leave empty for all)</span>
                </label>
                {users.filter(u => u.orgId === fFrom).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No users in this org</p>
                ) : (
                  <div className="max-h-28 overflow-y-auto rounded border border-slate-200 bg-white divide-y divide-slate-50">
                    {users.filter(u => u.orgId === fFrom).map(u => (
                      <label key={u.clerkUserId} className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50">
                        <input type="checkbox" checked={fAllowedUsers.has(u.clerkUserId)}
                          onChange={() => toggleAllowedUser(u.clerkUserId)}
                          className="h-3.5 w-3.5 rounded border-slate-300 accent-indigo-600" />
                        <span className="text-xs text-slate-700 truncate">{u.displayName ?? u.email ?? u.clerkUserId}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">
                  Restrict to groups <span className="text-slate-400 font-normal">(optional — leave empty for all)</span>
                </label>
                {groups.filter(g => g.orgId === fFrom).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No groups in this org</p>
                ) : (
                  <div className="max-h-28 overflow-y-auto rounded border border-slate-200 bg-white divide-y divide-slate-50">
                    {groups.filter(g => g.orgId === fFrom).map(g => (
                      <label key={g.id} className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50">
                        <input type="checkbox" checked={fAllowedGroups.has(g.id)}
                          onChange={() => toggleAllowedGroup(g.id)}
                          className="h-3.5 w-3.5 rounded border-slate-300 accent-indigo-600" />
                        <span className="text-xs text-slate-700 truncate">{g.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving}>{saving ? "Saving…" : "Create rule"}</Button>
          </div>
        </form>
      )}

      {!rules || rules.length === 0 ? (
        <p className="text-sm text-muted-foreground">No cross-org rules found.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-100">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">From Org</th>
                <th className="px-4 py-2.5 text-left font-medium">To Org</th>
                <th className="px-4 py-2.5 text-left font-medium">Product</th>
                <th className="px-4 py-2.5 text-left font-medium">Role mapping</th>
                <th className="px-4 py-2.5 text-left font-medium">Scope</th>
                <th className="px-4 py-2.5 text-left font-medium">Status</th>
                <th className="px-4 py-2.5 text-left font-medium">Created</th>
                <th className="px-4 py-2.5 text-left font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 bg-white">
              {rules.map((r) => {
                const overriddenBy = overriddenByProducts.get(r.id);
                const shadowsGlobal = overridesGlobalRule.has(r.id);
                return (
                <tr key={r.id} className={`hover:bg-slate-50/50 ${overriddenBy ? "bg-amber-50/30" : ""}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-xs font-medium text-slate-800 truncate max-w-[140px]">
                      {r.fromOrgName ?? r.fromOrgId}
                    </div>
                    <div className="font-mono text-[10px] text-slate-400 truncate max-w-[140px]">{r.fromOrgId}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    {r.toOrgId ? (
                      <>
                        <div className="text-xs font-medium text-slate-800 truncate max-w-[140px]">
                          {r.toOrgName ?? r.toOrgId}
                        </div>
                        <div className="font-mono text-[10px] text-slate-400 truncate max-w-[140px]">{r.toOrgId}</div>
                      </>
                    ) : (
                      <Badge color="amber">all orgs</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {r.productId ? (
                      <div className="flex flex-col gap-1">
                        <span>{products.find(p => p.id === r.productId)?.name ?? r.productId}</span>
                        {shadowsGlobal && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-600 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5 w-fit">
                            ↑ overrides global
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <span className="text-slate-400 italic">all</span>
                        {overriddenBy && overriddenBy.length > 0 && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 w-fit"
                            title={`Tool-level rules take precedence for: ${overriddenBy.join(", ")}`}
                          >
                            ⚠ overridden for {overriddenBy.join(", ")}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-col gap-0.5 text-[10px] text-slate-500">
                      <span>admin → {r.adminMapsTo}</span>
                      <span>manager → {r.managerMapsTo}</span>
                      <span>member → {r.memberMapsTo}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 max-w-[160px]">
                    {(!r.allowedUserIds || r.allowedUserIds.length === 0) && (!r.allowedGroupIds || r.allowedGroupIds.length === 0) ? (
                      <span className="text-[10px] text-slate-400 italic">all users &amp; groups</span>
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        {r.allowedUserIds && r.allowedUserIds.length > 0 && r.allowedUserIds.map(uid => (
                          <span key={uid} className="text-[10px] text-slate-600 truncate" title={uid}>
                            👤 {userMap.get(uid) ?? uid}
                          </span>
                        ))}
                        {r.allowedGroupIds && r.allowedGroupIds.length > 0 && r.allowedGroupIds.map(gid => (
                          <span key={gid} className="text-[10px] text-slate-600 truncate" title={gid}>
                            ⬡ {groupMap.get(gid) ?? gid}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge color={statusColor(r.status)}>{r.status}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {r.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            variant="default"
                            className="text-xs"
                            disabled={busy === r.id}
                            onClick={() => void handleApprove(r)}
                          >
                            {busy === r.id ? "…" : "Approve"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
                            disabled={busy === r.id}
                            onClick={() => void handleReject(r)}
                          >
                            {busy === r.id ? "…" : "Reject"}
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700 text-xs"
                        disabled={busy === r.id}
                        onClick={() => void handleDelete(r)}
                      >
                        {busy === r.id ? "…" : "Delete"}
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

// ── Groups tab ─────────────────────────────────────────────────────────────

const GROUP_TOOL_ROLES = ["tool-admin", "tool-manager", "tool-member", "tool-viewer", "denied"] as const;

interface GroupRowProps {
  group: SuiteGroup;
  users: AdminUser[];
  orgs: AdminOrg[];
  products: AdminProduct[];
  deleting: boolean;
  onDelete: () => void;
  getToken: () => Promise<string | null>;
}

function GroupRow({ group, users, orgs, products, deleting, onDelete, getToken }: GroupRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [members, setMembers] = useState<GroupMember[] | null>(null);
  const [toolRoles, setToolRoles] = useState<GroupToolRole[] | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [addUserIds, setAddUserIds] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [settingRole, setSettingRole] = useState<string | null>(null);

  const orgName = orgs.find(o => o.id === group.orgId)?.name ?? group.orgId;
  const userMap = useMemo(
    () => new Map(users.map(u => [u.clerkUserId, u.displayName ?? u.email ?? u.clerkUserId])),
    [users]
  );
  const orgUsers = useMemo(() => users.filter(u => u.orgId === group.orgId), [users, group.orgId]);

  async function toggle() {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (members !== null) return;
    setLoadingDetail(true);
    try {
      const [m, t] = await Promise.all([
        adminGetGroupMembers(getToken, group.id),
        adminGetGroupToolRoles(getToken, group.id),
      ]);
      setMembers(m);
      setToolRoles(t);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load group details");
    } finally {
      setLoadingDetail(false);
    }
  }

  function toggleAddUser(id: string) {
    setAddUserIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function addMembers() {
    if (addUserIds.size === 0) return;
    setAdding(true);
    try {
      await Promise.all([...addUserIds].map(id => adminAddGroupMember(getToken, group.id, id)));
      const count = addUserIds.size;
      toast.success(`Added ${count} member${count !== 1 ? "s" : ""} to ${group.name}`);
      setMembers(await adminGetGroupMembers(getToken, group.id));
      setAddUserIds(new Set());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to add members");
    } finally {
      setAdding(false);
    }
  }

  async function removeMember(userId: string) {
    setRemoving(userId);
    try {
      await adminRemoveGroupMember(getToken, group.id, userId);
      toast.success(`Removed ${userMap.get(userId) ?? userId} from ${group.name}`);
      setMembers((prev) => prev?.filter(m => m.clerkUserId !== userId) ?? null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to remove member");
    } finally {
      setRemoving(null);
    }
  }

  async function setToolRole(productId: string, role: string) {
    setSettingRole(productId);
    try {
      await adminSetGroupToolRole(getToken, group.id, { productId, role });
      toast.success(`Set ${products.find(p => p.id === productId)?.name ?? productId} role to ${role}`);
      setToolRoles(await adminGetGroupToolRoles(getToken, group.id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to set role");
    } finally {
      setSettingRole(null);
    }
  }

  const memberIds = useMemo(() => new Set((members ?? []).map(m => m.clerkUserId)), [members]);
  const availableUsers = useMemo(() => orgUsers.filter(u => !memberIds.has(u.clerkUserId)), [orgUsers, memberIds]);

  return (
    <li className="border-b border-slate-100 last:border-b-0">
      <div className="flex w-full items-center gap-1 pr-2 hover:bg-slate-50 transition-colors">
        <button
          className="flex flex-1 min-w-0 items-center gap-3 px-4 py-3 text-left"
          onClick={() => void toggle()}
        >
          <Layers className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900">{group.name}</div>
            <div className="flex items-center gap-2 mt-0.5">
              {group.description && (
                <span className="text-xs text-slate-400">{group.description}</span>
              )}
              <span className="text-xs text-slate-400">· {orgName}</span>
            </div>
          </div>
          <span className="ml-auto flex-shrink-0">
            {expanded
              ? <ChevronDown className="h-4 w-4 text-slate-400" />
              : <ChevronRight className="h-4 w-4 text-slate-400" />}
          </span>
        </button>
        <button
          className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 transition-colors flex-shrink-0"
          title="Delete group"
          disabled={deleting}
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-slate-50 bg-slate-50/50 px-4 pb-4 pt-3 space-y-5">
          {loadingDetail && (
            <div className="py-2 text-xs text-slate-400">Loading…</div>
          )}
          {!loadingDetail && members !== null && (
            <>
              {/* Members */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Members ({members.length})
                  </span>
                </div>

                {/* Add members — multi-select checkbox list */}
                {availableUsers.length > 0 && (
                  <div className="mb-3 space-y-2">
                    <div className="max-h-36 overflow-y-auto rounded border border-slate-200 bg-white divide-y divide-slate-50">
                      {availableUsers.map(u => (
                        <label
                          key={u.clerkUserId}
                          className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 hover:bg-slate-50"
                        >
                          <input
                            type="checkbox"
                            checked={addUserIds.has(u.clerkUserId)}
                            onChange={() => toggleAddUser(u.clerkUserId)}
                            className="h-3.5 w-3.5 rounded border-slate-300 accent-indigo-600"
                          />
                          <span className="text-xs text-slate-700">
                            {u.displayName ?? u.email ?? u.clerkUserId}
                          </span>
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        disabled={addUserIds.size === 0 || adding}
                        onClick={() => void addMembers()}
                      >
                        {adding
                          ? "Adding…"
                          : addUserIds.size > 0
                          ? `Add ${addUserIds.size} member${addUserIds.size !== 1 ? "s" : ""}`
                          : "Add members"}
                      </Button>
                    </div>
                  </div>
                )}

                {members.length === 0 ? (
                  <p className="text-xs text-slate-400">No members yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-100 rounded-md border border-slate-100 bg-white">
                    {members.map(m => (
                      <li key={m.clerkUserId} className="flex items-center justify-between gap-3 px-3 py-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <MemberAvatar name={userMap.get(m.clerkUserId) ?? null} imageUrl={null} className="h-6 w-6 text-[10px]" />
                          <span className="truncate text-xs font-medium text-slate-800">
                            {userMap.get(m.clerkUserId) ?? m.clerkUserId}
                          </span>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={removing === m.clerkUserId}
                          onClick={() => void removeMember(m.clerkUserId)}
                          className="h-6 w-6 text-red-500 hover:bg-red-50 hover:text-red-700"
                          aria-label="Remove member"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Tool roles */}
              <div>
                <div className="mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Tool roles
                  </span>
                </div>
                <div className="rounded-md border border-slate-100 bg-white divide-y divide-slate-50">
                  {products.map((prod) => {
                    const productId = prod.id;
                    const current = toolRoles?.find(r => r.productId === productId)?.role ?? "";
                    return (
                      <div key={productId} className="flex items-center justify-between gap-3 px-3 py-2">
                        <span className="text-xs font-medium text-slate-700">{prod.name}</span>
                        <select
                          value={current}
                          disabled={settingRole === productId}
                          onChange={e => void setToolRole(productId, e.target.value)}
                          className="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
                        >
                          <option value="">— no role —</option>
                          {GROUP_TOOL_ROLES.map(r => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-xs text-slate-400">
                  Members inherit the highest role across all their groups.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}

function GroupsTab({ getToken }: { getToken: () => Promise<string | null> }) {
  const [groups, setGroups] = useState<SuiteGroup[] | null>(null);
  const [orgs, setOrgs] = useState<AdminOrg[] | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [fOrg, setFOrg] = useState("");
  const [fName, setFName] = useState("");
  const [fDesc, setFDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, o, u, p] = await Promise.all([
        adminListGroups(getToken),
        adminListOrgs(getToken),
        adminListUsers(getToken),
        adminListProducts(getToken),
      ]);
      setGroups(g); setOrgs(o); setUsers(u); setProducts(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load groups");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  async function handleDeleteGroup(groupId: string, groupName: string) {
    setDeleting(groupId);
    try {
      await adminDeleteGroup(getToken, groupId);
      toast.success(`Group "${groupName}" deleted`);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete group");
    } finally {
      setDeleting(null);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!fOrg || !fName.trim()) return;
    setSaving(true);
    try {
      await adminCreateGroup(getToken, {
        orgId: fOrg,
        name: fName.trim(),
        ...(fDesc.trim() ? { description: fDesc.trim() } : {}),
      });
      toast.success(`Group "${fName.trim()}" created`);
      setShowForm(false);
      setFOrg(""); setFName(""); setFDesc("");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create group");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <ul className="divide-y divide-slate-100 rounded-md border border-slate-100">
      {[...Array(3)].map((_, i) => (
        <li key={i} className="flex items-center gap-3 px-4 py-3">
          <div className="h-4 w-4 animate-pulse rounded bg-slate-200" />
          <div className="space-y-1.5">
            <div className="h-3.5 w-48 animate-pulse rounded bg-slate-200" />
            <div className="h-3 w-32 animate-pulse rounded bg-slate-100" />
          </div>
        </li>
      ))}
    </ul>
  );

  if (error) return <ErrorBanner message={error} onRetry={load} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ Create Group"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={(e) => void handleCreate(e)} className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Organisation *</label>
              <select value={fOrg} onChange={e => setFOrg(e.target.value)} required
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm">
                <option value="">Select org…</option>
                {(orgs ?? []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Group name *</label>
              <input
                value={fName} onChange={e => setFName(e.target.value)} required
                placeholder="e.g. IP Team"
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-600 block mb-1">Description (optional)</label>
              <input
                value={fDesc} onChange={e => setFDesc(e.target.value)}
                placeholder="What is this group for?"
                className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving}>{saving ? "Creating…" : "Create group"}</Button>
          </div>
        </form>
      )}

      {!groups || groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">No groups found. Create one above.</p>
      ) : (
        <ul className="rounded-md border border-slate-100">
          {groups.map(g => (
            <GroupRow
              key={g.id}
              group={g}
              users={users ?? []}
              orgs={orgs ?? []}
              products={products}
              deleting={deleting === g.id}
              onDelete={() => void handleDeleteGroup(g.id, g.name)}
              getToken={getToken}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Products tab ───────────────────────────────────────────────────────────

function ProductsTab({ getToken }: { getToken: () => Promise<string | null> }) {
  const [products, setProducts] = useState<AdminProduct[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createId, setCreateId] = useState("");
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setProducts(await adminListProducts(getToken));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  async function handleDelete(productId: string) {
    setBusy(productId);
    try {
      await adminDeleteProduct(getToken, productId);
      toast.success(`Deleted product "${productId}"`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    try {
      await adminCreateProduct(getToken, {
        id: createId.trim(),
        name: createName.trim(),
        description: createDesc.trim() || undefined,
      });
      toast.success(`Registered product "${createId.trim()}"`);
      setShowCreate(false);
      setCreateId("");
      setCreateName("");
      setCreateDesc("");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Create failed");
    } finally {
      setCreating(false);
    }
  }

  if (loading) return (
    <ul className="divide-y divide-slate-100 rounded-md border border-slate-100">
      {[...Array(4)].map((_, i) => (
        <li key={i} className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 animate-pulse rounded bg-slate-200" />
            <div className="space-y-1.5">
              <div className="h-3.5 w-32 animate-pulse rounded bg-slate-200" />
              <div className="h-3 w-48 animate-pulse rounded bg-slate-100" />
            </div>
          </div>
          <div className="h-7 w-16 animate-pulse rounded bg-slate-100" />
        </li>
      ))}
    </ul>
  );

  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!products) return null;

  const sorted = [...products].sort((a, b) => a.id.localeCompare(b.id));

  return (
    <div className="space-y-4">
      <ul className="divide-y divide-slate-100 rounded-md border border-slate-100">
        {sorted.length === 0 && (
          <li className="px-4 py-8 text-center text-sm text-slate-400">No products registered yet.</li>
        )}
        {sorted.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-indigo-50 text-indigo-600">
                <Package className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900">{p.name}</span>
                  <Badge color="slate">{p.id}</Badge>
                </div>
                {p.description && (
                  <p className="mt-0.5 truncate text-xs text-slate-400">{p.description}</p>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              {confirmDelete === p.id ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-red-600 font-medium">Delete all data?</span>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={busy === p.id}
                    onClick={() => void handleDelete(p.id)}
                  >
                    {busy === p.id ? "Deleting…" : "Confirm"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirmDelete(null)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setConfirmDelete(p.id)}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {showCreate ? (
        <form
          onSubmit={(e) => void handleCreate(e)}
          className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3"
        >
          <p className="text-sm font-medium text-slate-700">Register new product</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-slate-500 font-medium">Product ID (shortname)</label>
              <input
                required
                pattern="[a-z0-9\-]+"
                title="Lowercase letters, numbers, and hyphens only"
                value={createId}
                onChange={(e) => setCreateId(e.target.value)}
                placeholder="e.g. spec"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500 font-medium">Display name</label>
              <input
                required
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g. Spec Builder"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-500 font-medium">Description (optional)</label>
            <input
              value={createDesc}
              onChange={(e) => setCreateDesc(e.target.value)}
              placeholder="Short description"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={creating}>
              {creating ? "Registering…" : "Register product"}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setShowCreate(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Register product
        </Button>
      )}
    </div>
  );
}

// ── Settings tab ────────────────────────────────────────────────────────────

function SettingsTab({ getToken }: { getToken: () => Promise<string | null> }) {
  const [emails, setEmails] = useState<string[]>([]);
  const [envFallback, setEnvFallback] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminGetNotificationEmails(getToken);
      setEmails(data.emails);
      setEnvFallback(data.envFallback);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  function addEmail() {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (emails.includes(trimmed)) {
      toast.error("That address is already in the list");
      return;
    }
    setEmails(prev => [...prev, trimmed]);
    setInputValue("");
  }

  function removeEmail(email: string) {
    setEmails(prev => prev.filter(e => e !== email));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await adminSetNotificationEmails(getToken, emails);
      toast.success("Notification recipients saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleSendTest() {
    setSendingTest(true);
    try {
      const result = await adminSendTestNotificationEmail(getToken);
      toast.success(`Test email sent to ${result.sentTo.join(", ")}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  }

  if (loading) return (
    <div className="space-y-2">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-10 w-full animate-pulse rounded-md bg-slate-100" />
      ))}
    </div>
  );

  if (error) return <ErrorBanner message={error} onRetry={load} />;

  return (
    <div className="space-y-6 max-w-xl">
      <form onSubmit={(e) => void handleSave(e)} className="space-y-4">
        <div>
          <p className="text-sm text-slate-600 mb-3">
            These addresses receive an email whenever someone joins the waitlist.
            {envFallback && emails.length === 0 && (
              <span className="block mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                No DB recipients set — currently falling back to env var:{" "}
                <span className="font-mono font-medium">{envFallback}</span>
              </span>
            )}
            {envFallback && emails.length > 0 && (
              <span className="block mt-1 text-xs text-slate-400">
                Env var <span className="font-mono">{envFallback}</span> is overridden by the list below.
              </span>
            )}
            {!envFallback && emails.length === 0 && (
              <span className="block mt-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                No recipients configured — waitlist emails will be skipped.
              </span>
            )}
          </p>

          {emails.length > 0 && (
            <ul className="mb-3 divide-y divide-slate-100 rounded-md border border-slate-200">
              {emails.map(email => (
                <li key={email} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="text-sm text-slate-800 font-mono">{email}</span>
                  <button
                    type="button"
                    onClick={() => removeEmail(email)}
                    className="text-red-400 hover:text-red-600 transition-colors p-1 rounded hover:bg-red-50"
                    aria-label={`Remove ${email}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-2">
            <input
              type="email"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
              placeholder="add@example.com"
              className="flex-1 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
            <Button type="button" variant="outline" size="sm" onClick={addEmail}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={sendingTest}
            onClick={() => void handleSendTest()}
          >
            {sendingTest ? (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            )}
            {sendingTest ? "Sending…" : "Send test email"}
          </Button>
          <Button type="submit" size="sm" disabled={saving}>
            {saving ? "Saving…" : "Save recipients"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ── Tab shell ──────────────────────────────────────────────────────────────

type Tab = "users" | "orgs" | "grants" | "cross-org" | "groups" | "products" | "settings";

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: "users", label: "Users", icon: Users },
  { id: "orgs", label: "Organisations", icon: Building2 },
  { id: "grants", label: "Org Grants", icon: Key },
  { id: "cross-org", label: "Cross-Org Rules", icon: ShieldCheck },
  { id: "groups", label: "Groups", icon: Layers },
  { id: "products", label: "Products", icon: Package },
  { id: "settings", label: "Settings", icon: RefreshCw },
];

function AdminDashboard() {
  const { getToken } = useAuth();
  const [tab, setTab] = useState<Tab>("users");
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    adminIsSuperAdmin(getToken)
      .then(setIsSuperAdmin)
      .catch(() => setIsSuperAdmin(false))
      .finally(() => setChecking(false));
  }, [getToken]);

  if (checking) {
    return <div className="py-16 text-center text-sm text-muted-foreground">Checking access…</div>;
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center rounded-md border border-slate-100 bg-slate-50 px-6 py-16 text-center">
        <ShieldCheck className="mb-3 h-10 w-10 text-slate-400" />
        <h2 className="text-base font-semibold text-slate-700">Super-admin access required</h2>
        <p className="mt-1 max-w-sm text-sm text-slate-500">
          This dashboard is only available to super-admins. Your user ID must be listed in the
          <code className="mx-1 rounded bg-slate-100 px-1 py-0.5 text-xs">ADMIN_USER_IDS</code>
          environment variable.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={[
              "flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
              tab === id
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300",
            ].join(" ")}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <section>
          <SectionHeader icon={Users} title="All Users" />
          <UsersTab getToken={getToken} />
        </section>
      )}
      {tab === "orgs" && (
        <section>
          <SectionHeader icon={Building2} title="Organisations" />
          <OrgsTab getToken={getToken} />
        </section>
      )}
      {tab === "grants" && (
        <section>
          <SectionHeader icon={Key} title="Org-Suite Grants" />
          <GrantsTab getToken={getToken} />
        </section>
      )}
      {tab === "cross-org" && (
        <section>
          <SectionHeader icon={ShieldCheck} title="Cross-Org Rules" />
          <CrossOrgRulesTab getToken={getToken} />
        </section>
      )}
      {tab === "groups" && (
        <section>
          <SectionHeader icon={Layers} title="Groups" />
          <GroupsTab getToken={getToken} />
        </section>
      )}
      {tab === "products" && (
        <section>
          <SectionHeader icon={Package} title="Products" />
          <ProductsTab getToken={getToken} />
        </section>
      )}
      {tab === "settings" && (
        <section>
          <SectionHeader icon={RefreshCw} title="Notification Email Recipients" />
          <SettingsTab getToken={getToken} />
        </section>
      )}
    </div>
  );
}

export default function AdminPage() {
  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <header className="mb-8 flex items-center gap-3">
        <ShieldCheck className="h-8 w-8 text-red-600" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">Super-Admin Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage suite users, organisations, and access grants.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ClerkLoaded>
            <SignedIn>
              <UserButton />
            </SignedIn>
          </ClerkLoaded>
        </div>
      </header>

      <ClerkLoading>
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 w-full animate-pulse rounded-md bg-slate-100" />
          ))}
        </div>
      </ClerkLoading>

      <ClerkLoaded>
        <SignedOut>
          <div className="flex flex-col items-center justify-center rounded-md border border-slate-100 bg-slate-50 px-6 py-16 text-center">
            <ShieldCheck className="mb-3 h-10 w-10 text-slate-400" />
            <h2 className="text-base font-semibold text-slate-700">Sign in to continue</h2>
            <p className="mt-1 mb-4 max-w-sm text-sm text-slate-500">
              You must be signed in as a super-admin to access this page.
            </p>
            <SignInButton mode="modal">
              <Button>Sign in</Button>
            </SignInButton>
          </div>
        </SignedOut>
        <SignedIn>
          <AdminDashboard />
        </SignedIn>
      </ClerkLoaded>
    </div>
  );
}
